import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ButtonClassNames } from '../../../../models/Common';
import { CustomMUIButton } from '../../buttons/CustomMUIButton';
import sharePromo3MonthsMobile from '../../../../assets/images/SharePromo3MonthsMobile.png';
import sharePromo3MonthsTablet from '../../../../assets/images/SharePromo3MonthsTablet.png';
import sharePromo3MonthsDesktop from '../../../../assets/images/SharePromo3MonthsDesktop.png';
import { useWindowDimension } from '../../../../hooks/useWindowDimension';
import { ImgWebp } from '../../ImgWeb';
import { SharePromo3Month } from '../../../servicePaymentParts/SharePromoBRIZTVPremium';
import DateVO from '../../../../core/valueObjects/DateVO';
import { CustomNumberFormat } from '../../CustomNumberFormat';

type ValueType = 'time' | 'money';

interface IShareData {
  type: ValueType;
  description: string;
  value?: number | string;
  hint?: string;
}

interface IPromoShareThreeMonths {
  shareCalculation: SharePromo3Month;
  handlePay?: () => void;
  handleActivate?: () => void;
  handleClose: () => void;
  customShareName: string;
  buttonShareName: string;
}

const defaultProps = {
  handlePay: undefined,
  handleActivate: undefined,
};

export const PromoShareThreeMonths: FC<IPromoShareThreeMonths> = ({
  shareCalculation,
  handlePay,
  handleActivate,
  handleClose,
  customShareName,
  buttonShareName,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowDimension();
  const payAmount = shareCalculation?.amount || '';
  const isNeedToPay = !!shareCalculation?.payAmount;
  const staticIpPrice = shareCalculation?.view?.hybrid?.[0].ex_amo.find((el) => el.name.includes('IP'))?.amo;
  const activationShareStart = format(DateVO.create(shareCalculation.fromDate).get(), 'dd.MM.yyyy');

  const getImage = (screenWidth) => {
    if (screenWidth >= 1280) return sharePromo3MonthsDesktop;
    if (screenWidth >= 768) return sharePromo3MonthsTablet;
    return sharePromo3MonthsMobile;
  };

  const shareData: IShareData[] = [
    {
      type: 'time',
      description: t('modals.promoShare3MonthTermValidity'),
      value: shareCalculation?.view?.hybrid?.[0]?.num || '',
    },
    {
      type: 'money',
      description: t('modals.promoShare3MonthCostService'),
      value: shareCalculation?.view?.hybrid?.[0]?.total_clear_amo || '',
    },
    { type: 'money', description: t('common.discount'), value: shareCalculation?.discountAmount || '' },
    {
      type: 'money',
      description: t('servicePayment.staticIp'),
      value: staticIpPrice,
      hint: t('modals.promoShare3MonthDiscountNotApplicable'),
    },
  ];

  const handleActivateShare = () => {
    if (isNeedToPay && handlePay) {
      handlePay();
    }
    if (!isNeedToPay && handleActivate) {
      handleActivate();
      handleClose();
    }
  };

  return (
    <div className="connect-share">
      <div className="connect-share__image">
        <ImgWebp fallback={getImage(width)} alt="promo-share" />
      </div>
      <div className="connect-share__content">
        <div className="connect-share__content__title">
          <p className="connect-share__content__title-description">{t('modals.promoShare3MonthConnect')}</p>
          <p className="connect-share__content__title-name">{customShareName}</p>
        </div>
        <div className="connect-share__content__bill">
          <ul className="connect-share__content__bill__services">
            {shareData.map(
              (el) =>
                el.value && (
                  <li key={el.description + el.value}>
                    <p>
                      <span data-text="description">{el.description}</span>
                      {el.hint ? <span data-text="hint">{el.hint}</span> : null}
                    </p>
                    <p>
                      <span>
                        {el.type === 'money' ? (
                          <CustomNumberFormat value={el.value} suffix={t('common.uah')} />
                        ) : (
                          t('servicePayment.sharePromoThreeMonthsHowLong', { length: el.value })
                        )}
                      </span>
                    </p>
                  </li>
                ),
            )}
          </ul>
          <hr />
          <div className="connect-share__content__bill__total">
            <span data-text="description">{t('modals.promoShare3MonthBeforePayment')}</span>
            <span data-text="value">
              <CustomNumberFormat value={payAmount} suffix={t('common.uah')} />
            </span>
          </div>
        </div>
        <div className="connect-share__btn-group">
          <CustomMUIButton
            label={buttonShareName || t('buttons.pay')}
            handleClick={handleActivateShare}
            className={ButtonClassNames.PrimaryFulfilled}
            isBold
          />
        </div>
        <div className="connect-share__terms">
          <p>{t('modals.promoShare3MonthPackageActivationFrom', { activationDate: activationShareStart })}</p>
        </div>
      </div>
    </div>
  );
};

PromoShareThreeMonths.defaultProps = defaultProps;
